import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import './RecordingDetailsContainer.css';
import { sessionsDetailsDesktopSkeletonScreen } from '../../../SkeletonScreens/SkeletonScreen';
import { getFormattedDateWithTimeZone, getTimeInHrMinAndSec } from '../../../Utils/DateFormatUtils';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { trackAnalyticsTypeSwitcher } from '../../../Telemetry/Mixpanel/Features/analyticsTracker';
import {
  AnalyticsTypeSwitcher,
  RECORDING_DETAILS,
  RecordingSourceType
} from '../../../Telemetry/Mixpanel/Constants/mixpanelConstants';
import { getTrimString } from '../../../Utils/StringUtils';
import RecordingDetailsStats from './Stats/RecordingDetailsStats';
import RecordingViewsTable from './Table/RecordingViewsTable';
import DateRangeSelector from '../../../Components/DateRangeSelector/DateRangeSelector';
import WebinarTypeText from '../../../Components/WebinarTypeText/WebinarTypeText';
import moment from 'moment-timezone';
import ViewsChart from './Chart/ViewsChart';
import { RecordingSource, RobotMessageState } from '../../../Utils/Constants';
import RobotMessageComponent from '../../../Components/RobotMessageComponent/RobotMessageComponent';
import { getRecordingDetails } from '../../../Reducks/RecordingDetails/RecordingDetailsActions';
import { filterRecordingViews } from '../../../Reducks/RecordingDetails/RecordingDetailsActions';

const RecordingNameHeading = ({ recordingDetails = {}, t }) => {
  const formattedDateTime = getFormattedDateWithTimeZone(recordingDetails.datePublished, recordingDetails.timeZone);
  const { videoLengthInMillis } = recordingDetails;
  let formattedVideoLength = getTimeInHrMinAndSec(moment.duration(videoLengthInMillis));
  return (
    <div className="recordingNameHeading">
      <WebinarTypeText type={recordingDetails.recordingSource} />
      <div className="title">{recordingDetails.name}</div>
      <div className="subTitle">
        <div>{t('recordingDetails.published', { formattedDateTime })}</div>
        {[RecordingSource.G2S, RecordingSource.G2S_EMBEDDED].includes(recordingDetails.recordingSource) && (
          <div>{t('recordingDetails.videoLength', { formattedVideoLength })}</div>
        )}
      </div>
    </div>
  );
};

export class RecordingDetailsContainer extends Component {
  static contextTypes = {
    t: PropTypes.func.isRequired
  };

  static propTypes = {
    isBig: PropTypes.bool,
    reduced_recording_details: PropTypes.object,
    getRecordingDetails: PropTypes.func,
    filterRecordingViews: PropTypes.func,
    user: PropTypes.object
  };
  componentDidMount() {
    const { match } = this.props;
    this.props.getRecordingDetails(match.params.recordingKey, match.params.recordingSource);
    trackAnalyticsTypeSwitcher(
      AnalyticsTypeSwitcher.RECORDING_DETAILS,
      match.params.recordingSource === RecordingSource.G2W
        ? RecordingSourceType.STANDALONE
        : match.params.recordingSource === RecordingSource.G2S
        ? RecordingSourceType.GOTOSTAGE
        : RecordingSourceType.GOTOSTAGE_EMBEDDED
    );
  }
  render() {
    const { recordingDetails, fetching, error } = this.props.reduced_recording_details;
    const { isBig } = this.props;
    const { t } = this.context;
    if (fetching) {
      return sessionsDetailsDesktopSkeletonScreen();
    } else if (error || recordingDetails === null) {
      return (
        <RobotMessageComponent
          type={
            error && error.response && [403, 404].includes(error.response.status)
              ? RobotMessageState.NOT_FOUND_STATE
              : RobotMessageState.ERROR_STATE
          }
        />
      );
    }
    const recordingSource = recordingDetails.recordingSource;
    const recordingNameComponent = <RecordingNameHeading recordingDetails={recordingDetails} t={t} />;
    const desktopView = recordingDetails.recordingSource !== null && (
      <div className="recordingDetailsDesktop">
        <div className="breadCrumbRow">
          {this.breadCrumbAndDateFilterComponent()}
          {recordingNameComponent}
        </div>
        {recordingSource === RecordingSource.G2S && <div className="statsRow">{this.recordingStatsComponent()}</div>}
        {this.recordingViewsChartComponent()}
        {this.recordingDetailsTableComponent()}
      </div>
    );

    const mobileView = recordingDetails.recordingSource !== null && (
      <div className="recordingDetailsMobile">
        <div className="breadCrumbRow">
          {this.breadCrumbAndDateFilterComponent()}
          {recordingNameComponent}
        </div>
        {recordingSource === RecordingSource.G2S && <div className="statsRow">{this.recordingStatsComponent()}</div>}
        {this.recordingViewsChartComponent()}
        {this.recordingDetailsTableComponent()}
      </div>
    );
    return <div className="recordingDetails">{isBig ? desktopView : mobileView}</div>;
  }

  getViews(recordingSource) {
    const { filteredG2WViews, filteredG2SViews, filteredG2SEmbeddedViews } = this.props.reduced_recording_details;
    return RecordingSource.G2W === recordingSource
      ? filteredG2WViews
      : RecordingSource.G2S === recordingSource
      ? filteredG2SViews
      : filteredG2SEmbeddedViews;
  }

  breadCrumbAndDateFilterComponent = () => {
    const { t } = this.context;
    const { fetching, recordingDetails, filter } = this.props.reduced_recording_details;
    const { isBig } = this.props;
    const mixpanelData = {
      action: RECORDING_DETAILS
    };

    return (
      <div className="d-flex justify-content-between align-items-center">
        <div className="overview d-inline-flex">
          <Breadcrumb>
            <BreadcrumbItem>
              <NavLink
                to="/analytics/recordings"
                onClick={() => trackAnalyticsTypeSwitcher(AnalyticsTypeSwitcher.RECORDING)}>
                {t('header.overview')}
              </NavLink>
            </BreadcrumbItem>
            <BreadcrumbItem active>{getTrimString(recordingDetails.name, isBig ? 32 : 16)}</BreadcrumbItem>
          </Breadcrumb>
        </div>

        <DateRangeSelector
          uniqueId={'dateFilterForRecordingDetails'}
          isBig={isBig}
          onApply={(fromTime, toTime) => {
            this.props.filterRecordingViews({ fromTime, toTime });
          }}
          fromDateTime={filter.fromTime}
          toDateTime={filter.toTime}
          disabled={fetching}
          mixpanelData={mixpanelData}
        />
      </div>
    );
  };

  recordingViewsChartComponent = () => {
    const { recordingDetails, filter, stats } = this.props.reduced_recording_details;
    const { isBig, user } = this.props;

    return (
      <div className="chartsRow">
        <ViewsChart
          totalViewCount={
            recordingDetails.recordingSource === RecordingSource.G2W
              ? stats.g2wViewCount
              : recordingDetails.recordingSource === RecordingSource.G2S
              ? stats.g2sViewCount
              : stats.g2sEmbeddedViewCount
          }
          user={user}
          fromTime={filter.fromTime}
          toTime={filter.toTime}
          views={this.getViews(recordingDetails.recordingSource)}
          isBig={isBig}
        />
      </div>
    );
  };

  recordingDetailsTableComponent = () => {
    const { isBig, reduced_recording_details } = this.props;
    const { recordingDetails } = reduced_recording_details;
    return (
      <RecordingViewsTable
        isBig={isBig}
        views={this.getViews(recordingDetails.recordingSource)}
        recordingSource={recordingDetails.recordingSource}
        searchByName={searchString => this.props.filterRecordingViews({ searchString })}
      />
    );
  };
  recordingStatsComponent = () => {
    const { stats } = this.props.reduced_recording_details;
    const { isBig } = this.props;

    return (
      <RecordingDetailsStats
        isBig={isBig}
        viewers={stats.g2sViewerCount}
        totalWatchTimeInMillis={stats.g2sTotalWatchTimeInMillis}
        averageWatchTimeInMillis={stats.g2sAverageWatchTimeInMillis}
        averagePercentageViewed={stats.g2sAveragePercentageViewed}
      />
    );
  };
}

export default withRouter(
  connect(
    state => ({
      reduced_recording_details: state.reduced_recording_details,
      user: state.reduced_user
    }),
    {
      getRecordingDetails: getRecordingDetails,
      filterRecordingViews: filterRecordingViews
    }
  )(RecordingDetailsContainer)
);



// WEBPACK FOOTER //
// ./src/GoToWebinar/Recordings/RecordingDetails/RecordingDetailsContainer.js